import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Select, Tag, Popconfirm, Switch } from 'antd';
import { TrInputNumber as InputNumber } from './TrInputNumber';
import { PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined } from '@ant-design/icons';
import {
  fetchProductCompatibilities,
  createCompatibility,
  updateCompatibility,
  deleteCompatibility,
  fetchBrands,
  fetchModels,
  createBrand,
  createModel,
  type BrandDto,
  type ModelDto,
  type ProductBrandCompatibilityDto
} from '../services/vehicleService';
import { useCanDelete } from '../hooks/useCanDelete';

interface VehicleCompatibilityManagerProps {
  productId: number;
  productName?: string;
}

const VehicleCompatibilityManager: React.FC<VehicleCompatibilityManagerProps> = ({ productId, productName }) => {
  const canDelete = useCanDelete();
  const [compatibilities, setCompatibilities] = useState<ProductBrandCompatibilityDto[]>([]);
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompatibility, setEditingCompatibility] = useState<ProductBrandCompatibilityDto | null>(null);
  const [form] = Form.useForm();
  const selectedBrandId = Form.useWatch('brandId', form);

  useEffect(() => {
    loadCompatibilities();
    loadBrands();
  }, [productId]);

  useEffect(() => {
    if (selectedBrandId) {
      loadModels(selectedBrandId);
    } else {
      setModels([]);
      form.setFieldsValue({ modelId: undefined });
    }
  }, [selectedBrandId]);

  const loadCompatibilities = async () => {
    try {
      setLoading(true);
      const data = await fetchProductCompatibilities(productId);
      setCompatibilities(data);
    } catch (error) {
      message.error('Uyumluluklar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const data = await fetchBrands();
      setBrands(data);
    } catch (error) {
      console.error('Markalar yüklenirken hata:', error);
    }
  };

  const loadModels = async (brandId: number) => {
    try {
      const data = await fetchModels(brandId);
      setModels(data);
    } catch (error) {
      console.error('Modeller yüklenirken hata:', error);
    }
  };

  const handleAdd = () => {
    setEditingCompatibility(null);
    form.resetFields();
    form.setFieldsValue({ isPrimary: false, isVerified: false });
    setModels([]);
    setModalVisible(true);
  };

  const handleEdit = (compatibility: ProductBrandCompatibilityDto) => {
    setEditingCompatibility(compatibility);
    form.setFieldsValue({
      brandId: compatibility.brandId,
      modelId: compatibility.modelId,
      yearFrom: compatibility.yearFrom,
      yearTo: compatibility.yearTo,
      isPrimary: compatibility.isPrimary,
      notes: compatibility.notes,
      isVerified: compatibility.isVerified
    });
    if (compatibility.brandId) {
      loadModels(compatibility.brandId);
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingCompatibility) {
        await updateCompatibility(editingCompatibility.id, {
          modelId: values.modelId || null,
          yearFrom: values.yearFrom || null,
          yearTo: values.yearTo || null,
          isPrimary: values.isPrimary,
          notes: values.notes,
          isVerified: values.isVerified
        });
        message.success('Uyumluluk güncellendi');
      } else {
        await createCompatibility({
          productId,
          brandId: values.brandId,
          modelId: values.modelId || null,
          yearFrom: values.yearFrom || null,
          yearTo: values.yearTo || null,
          isPrimary: values.isPrimary,
          notes: values.notes,
          isVerified: values.isVerified
        });
        message.success('Uyumluluk eklendi');
      }
      
      setModalVisible(false);
      form.resetFields();
      setModels([]);
      loadCompatibilities();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCompatibility(id);
      message.success('Uyumluluk silindi');
      loadCompatibilities();
    } catch (error) {
      message.error('Uyumluluk silinirken hata oluştu');
    }
  };

  const columns = [
    {
      title: 'Marka',
      dataIndex: 'brand',
      key: 'brand',
      render: (brand: ProductBrandCompatibilityDto['brand']) => brand.name},
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (model: ProductBrandCompatibilityDto['model']) => model ? model.name : '-'},
    {
      title: 'Yıl',
      key: 'year',
      render: (_: any, record: ProductBrandCompatibilityDto) => {
        const { yearFrom, yearTo } = record;
        if (yearFrom && yearTo) {
          return yearFrom === yearTo ? yearFrom.toString() : `${yearFrom}-${yearTo}`;
        } else if (yearFrom) {
          return `${yearFrom}+`;
        }
        return '-';
      }},
    {
      title: 'Görünen Ad',
      dataIndex: 'displayName',
      key: 'displayName'},
    {
      title: 'Ana Uyumluluk',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (isPrimary: boolean) => (
        isPrimary ? <Tag color="green">Ana</Tag> : null
      )},
    {
      title: 'Onaylı',
      dataIndex: 'isVerified',
      key: 'isVerified',
      render: (isVerified: boolean) => (
        isVerified ? <Tag color="blue">Onaylı</Tag> : null
      )},
    {
      title: 'Notlar',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true},
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ProductBrandCompatibilityDto) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {canDelete && (
          <Popconfirm
            title="Bu uyumluluğu silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
          )}
        </Space>
      )},
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>
          <CarOutlined /> Araç Uyumluluğu
        </h3>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Uyumluluk Ekle
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={compatibilities}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
      />

      {/* Uyumluluk Ekleme/Düzenleme Modal */}
      <Modal
        title={editingCompatibility ? 'Uyumluluğu Düzenle' : 'Yeni Uyumluluk Ekle'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          setEditingCompatibility(null);
          form.resetFields();
          setModels([]);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="brandId"
            label="Marka"
            rules={[{ required: true, message: 'Lütfen marka seçin' }]}
          >
            <Select
              placeholder="Marka seçin"
              showSearch
              allowClear
              disabled={!!editingCompatibility}
              onChange={(value) => {
                if (value) {
                  loadModels(value);
                } else {
                  setModels([]);
                  form.setFieldsValue({ modelId: undefined });
                }
              }}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={brands.map(brand => ({ value: brand.id, label: brand.name }))}
              notFoundContent="Marka bulunamadı"
            />
          </Form.Item>
          <Form.Item
            name="modelId"
            label="Model (Opsiyonel)"
          >
            <Select
              placeholder={selectedBrandId ? "Model seçin (opsiyonel)" : "Önce marka seçin"}
              showSearch
              allowClear
              disabled={!selectedBrandId}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={models.map(model => ({ value: model.id, label: model.name }))}
              notFoundContent={selectedBrandId ? "Model bulunamadı" : "Önce marka seçin"}
            />
          </Form.Item>
          <Form.Item
            name="yearFrom"
            label="Başlangıç Yılı (Opsiyonel)"
          >
            <InputNumber
              placeholder="Örn: 2010"
              min={1900}
              max={2100}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="yearTo"
            label="Bitiş Yılı (Opsiyonel)"
          >
            <InputNumber
              placeholder="Örn: 2014"
              min={1900}
              max={2100}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="isPrimary"
            label="Ana Uyumluluk"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="isVerified"
            label="Onaylı"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="notes"
            label="Notlar"
          >
            <Input.TextArea rows={3} placeholder="Ek notlar..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VehicleCompatibilityManager;
